import * as compression from '@codegrade/compression';
import * as jupyter from '@codegrade/jupyter-notebooks';
import { Maybe } from '@codegrade/maybe';
import { NamedUlid } from '@codegrade/ulid';
import { array, datetime, expr, itertools, object, sort } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import { client as apiv2Client } from '@/client-v2';
import * as utils from '@/utils';
import * as fileUtils from '@/utils/files';
const storeBuilder = getStoreBuilder();
/** @returns The initial state. */
function makeInitialState() {
    return {
        outputs: {},
        kernels: {},
    };
}
const moduleBuilder = storeBuilder.module('jupyter', makeInitialState());
/**
 * Get a raw messages state from the store.
 *
 * @param state The current state of the entire store.
 * @param ids The identifiers to point to a conversation.
 * @returns The state of the messages from the store or undefined if it does not exist yet.
 */
function getOutputsState(state, ids) {
    return state.outputs[ids.documentId]?.[ids.cellId];
}
/**
 * Convert a `MimeMapData` item received in a websocket update to the Smithy representation.
 *
 * @param data The mime map entry to convert.
 * @returns The same mime map data in Smithy representation.
 */
function websocketMimeMapDataToSmithy(data) {
    return expr.match(data, 'where', {
        inline: inline => ({
            inline: inline.data.toCompressedBinary(),
        }),
        s3: s3 => ({
            link: {
                url: s3.url,
            },
        }),
    });
}
/**
 * Convert a `MimeMap` received in a websocket update to the Smithy representation.
 *
 * @param data The mime map to convert.
 * @returns The same mime map in Smithy representation.
 */
function websocketMimeMapToSmithy(data) {
    return object.mapObject(data, websocketMimeMapDataToSmithy);
}
/**
 * Convert an output received in a websocket update to the Smithy representation.
 *
 * We do this to reduce the amount of duplicated code.
 *
 * @param output The output to convert.
 * @returns The same output converted to Smithy representation.
 */
function websocketOutputToSmithy(output) {
    return {
        jupyterOutputId: output.partId,
        event: expr.match(output.event, 'what', {
            'execute-requested': () => ({
                executeRequested: {},
            }),
            'execute-interrupted': () => ({
                executeInterrupted: {},
            }),
            'execute-start': executeStart => ({
                executeStart: {
                    kernelId: executeStart.kernelId.extract(),
                    executionCount: executeStart.executionCount.extract(),
                },
            }),
            stream: stream => ({
                stream: {
                    kernelId: stream.kernelId.extract(),
                    name: stream.where,
                    value: websocketMimeMapDataToSmithy(stream.data),
                },
            }),
            'display-data': displayData => ({
                displayData: {
                    kernelId: displayData.kernelId.extract(),
                    displayId: displayData.displayId.extract(),
                    mimeData: websocketMimeMapToSmithy(displayData.data),
                },
            }),
            error: error => ({
                error: {
                    ...error,
                    kernelId: error.kernelId.extract(),
                },
            }),
            'execute-result': executeResult => ({
                executeResult: {
                    kernelId: executeResult.kernelId.extract(),
                    executionCount: executeResult.executionCount.extract(),
                    mimeData: websocketMimeMapToSmithy(executeResult.data),
                },
            }),
        }),
    };
}
/**
 * Download, if needed, and decompress mime output data.
 *
 * @param mimeOutput The output that will be downloaded if needed, and decompressed.
 * @returns A Promise that resolves the decompressed data.
 */
async function downloadJupyterMimeOutputDataEvent(mimeOutput) {
    const { JupyterMimeOutputDataEventValue } = await import('@codegrade/apiv2-client');
    return await JupyterMimeOutputDataEventValue.visit(mimeOutput, {
        inline: async (inline) => Maybe.of(await compression.decompressPromise(compression.CompressionResult.fromCompressedBinary(inline).unsafeCoerce())),
        link: async ({ url }) => Maybe.of(await fileUtils.downloadCompressedFileFromS3(url)),
        _: async (key) => {
            // eslint-disable-next-line no-console
            console.log(`Got unknown mime output data: ${key}`);
            return Maybe.empty();
        },
    });
}
/**
 * Get the entry for a given mime type and then parses it using the appropriate
 * parser.
 *
 * @param typ The type of the mime we're getting the entry for.
 * @param map The mapping of mime types.
 * @returns A Maybe of a string representing the entry for the mime type.
 */
async function getMimeMapEntry(typ, map) {
    const mimeOutputData = map[typ];
    if (mimeOutputData == null) {
        return Maybe.empty();
    }
    const data = await downloadJupyterMimeOutputDataEvent(mimeOutputData);
    if (data.isNothing()) {
        return Maybe.empty();
    }
    // Data is a JSON chunk, so to do anything with it we still need to JSON parse it.
    const decoded = JSON.parse(new TextDecoder().decode(data.value));
    return jupyter.mime.parsers[typ].decode(decoded).caseOf({
        Right: v => Maybe.of(v),
        Left: () => Maybe.empty(),
    });
}
/**
 * Process a mime map from jupyter output.
 *
 * @param map The map to process.
 * @returns The most important (ie highest priority) that mime output data that could be parsed.
 */
async function processMimeMap(map) {
    for (const key of jupyter.mime.RENDER_ORDER) {
        // eslint-disable-next-line no-await-in-loop
        const entry = await getMimeMapEntry(key, map);
        if (entry.isNothing()) {
            continue;
        }
        const value = entry.extract();
        if (jupyter.mime.isImage(key)) {
            // eslint-disable-next-line no-await-in-loop
            const textEntry = await getMimeMapEntry('text/plain', map);
            const altText = textEntry.orDefault('Rendered image in a Jupyter notebook');
            return Maybe.of(Object.freeze({ tag: key, value, altText }));
        }
        return Maybe.of(Object.freeze({ tag: key, value }));
    }
    return Maybe.empty();
}
/**
 * Process a APIv2 output chunk by mangling the data and potentially downloading any S3 data.
 *
 * @param output The output chunk to process.
 * @returns A promise that resolves to the fully downloaded output chunk.
 */
async function processOutput(output) {
    const { JupyterOutputEvent } = await import('@codegrade/apiv2-client');
    const createdAt = Maybe.encase(() => NamedUlid.parse('jupexec', output.jupyterOutputId))
        .ifNothing(() => 
    // eslint-disable-next-line no-console
    console.warn(`Failed to parse ULID: ${output.jupyterOutputId}`))
        .map(ulid => datetime.toMoment(ulid.time));
    const event = await JupyterOutputEvent.visit(output.event, {
        displayData: async (displayData) => {
            const data = await processMimeMap(displayData.mimeData);
            return {
                tag: 'display-data',
                kernelId: Maybe.fromNullable(displayData.kernelId),
                displayId: Maybe.fromNullable(displayData.displayId),
                mimeData: data.orDefault(Object.freeze({ tag: 'unknown' })),
            };
        },
        stream: async (stream) => {
            const downloaded = await downloadJupyterMimeOutputDataEvent(stream.value);
            const text = downloaded.mapOrDefault(data => new TextDecoder().decode(data), '');
            return {
                tag: 'stream',
                kernelId: Maybe.fromNullable(stream.kernelId),
                name: stream.name,
                parts: [{ outputId: output.jupyterOutputId, text }],
            };
        },
        executeResult: async (executeResult) => {
            const mimeData = await processMimeMap(executeResult.mimeData);
            return {
                tag: 'execute-result',
                kernelId: Maybe.fromNullable(executeResult.kernelId),
                createdAt,
                executionCount: Maybe.fromNullable(executeResult.executionCount),
                mimeData,
            };
        },
        error: async (error) => ({
            tag: 'error',
            kernelId: Maybe.fromNullable(error.kernelId),
            createdAt,
            traceback: error.traceback,
            executionCount: Maybe.fromNullable(error.executionCount),
        }),
        executeStart: async ({ kernelId, executionCount }) => ({
            tag: 'execute-start',
            kernelId: Maybe.fromNullable(kernelId),
            createdAt,
            executionCount: Maybe.fromNullable(executionCount),
        }),
        executeRequested: async () => ({
            tag: 'execute-requested',
            createdAt,
        }),
        executeInterrupted: async () => ({
            tag: 'execute-interrupted',
            createdAt,
        }),
        _: async (foundTag) => ({
            tag: '$unknown',
            foundTag,
        }),
    });
    return Object.freeze({
        jupyterOutputId: output.jupyterOutputId,
        data: Object.freeze(event),
    });
}
/**
 * Split all `stream` outputs consisting of multiple parts into multiple outputs with a single part.
 *
 * @param outputs The outputs to process.
 * @yields The same outputs as the input, except for the `stream` outputs which consist of exacly 1 part.
 * @returns An iterable of outputs.
 */
function* splitStreamOutputs(outputs) {
    for (const output of outputs) {
        if (output.data.tag !== 'stream' || output.data.parts.length === 1) {
            yield output;
        }
        else {
            yield* output.data.parts.map(part => ({
                jupyterOutputId: part.outputId,
                data: {
                    ...output.data,
                    parts: [part],
                },
            }));
        }
    }
}
/**
 * Merge consecutive `stream` outputs to the same stream into a single output.
 *
 * Note: this assumes that the outputs are already in the correct order.
 *
 * @param outputs The outputs to process.
 * @yields The same outputs as the input, except for the `stream` outputs to the
 * same stream which have been merged with other `stream` outputs directly
 * following them.
 * @returns An iterable with no consecutive `stream` outputs to the same stream.
 */
function* mergeStreamOutputs(outputs) {
    let currentStream = null;
    for (const output of outputs) {
        const data = output.data;
        if (currentStream != null &&
            !(data.tag === 'stream' &&
                data.name === currentStream.data.name &&
                data.kernelId.equals(currentStream.data.kernelId))) {
            yield currentStream;
            currentStream = null;
        }
        if (data.tag !== 'stream') {
            yield output;
        }
        else if (currentStream == null) {
            currentStream = {
                ...output,
                data: {
                    ...data,
                    parts: data.parts.slice(),
                },
            };
        }
        else {
            currentStream.data.parts.push(...data.parts);
        }
    }
    if (currentStream != null) {
        yield currentStream;
    }
}
/**
 * Merge two sequences of jupyter cell output.
 *
 * This function will return a fresh array, that contains all data from `curData` and any _new_ data
 * from `toAdd` (so events already in `curData` will be skipped). It will also remove data that is
 * no longer relevant, which any events that were created before a `execute-start` event.
 *
 * @param curData The data already in the store.
 * @param toAdd The data that should be added.
 * @returns A fresh array as described above.
 */
export function mergeOutputs(curData, toAdd) {
    const curDataSplitStream = Array.from(splitStreamOutputs(curData));
    const lastExecuteStart = array
        .findMax(el => [el.jupyterOutputId], itertools.filterBool(el => ['execute-start'].includes(el.data.tag), itertools.chainArrays(curDataSplitStream, toAdd)))
        .map(x => x.jupyterOutputId);
    let filteredData = itertools.chainArrays(curDataSplitStream, toAdd);
    if (lastExecuteStart.isJust()) {
        filteredData = itertools.filterBool(el => el.jupyterOutputId >= lastExecuteStart.value, filteredData);
    }
    const sortedData = sort.sortBy(filteredData, ({ jupyterOutputId }) => [jupyterOutputId]);
    const uniquedData = itertools.uniqueSorted(sortedData, (a, b) => a.jupyterOutputId === b.jupyterOutputId);
    return Object.freeze(Array.from(mergeStreamOutputs(uniquedData)));
}
/** The store for the Jupyter. */
export var JupyterStore;
(function (JupyterStore) {
    const commitClearAll = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAll');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        commitClearAll();
    });
    const commitOutputLoader = moduleBuilder.commit((state, { documentId, cellId, loader, }) => {
        const byConvoId = utils.vueSetDefault(state.outputs, documentId, {});
        const oldState = utils.vueSetDefault(byConvoId, cellId, {
            fullyLoaded: false,
            data: [],
        });
        utils.vueSet(oldState, 'fullLoader', loader);
    }, 'commitOutputLoader');
    const commitAddOutputData = moduleBuilder.commit((state, { documentId, cellId, dataToAdd, }) => {
        const byConvoId = utils.vueSetDefault(state.outputs, documentId, {});
        const oldState = utils.vueSetDefault(byConvoId, cellId, {
            fullyLoaded: false,
            data: [],
        });
        utils.vueSet(oldState, 'data', mergeOutputs(oldState.data, dataToAdd));
    }, 'commitAddOutputData');
    /**
     * Update the state of the outputs for the given cell to be full loaded.
     *
     * For cells that have not fully loaded the output getter will not return
     * anything, even if some outputs have already been loaded.
     */
    JupyterStore.commitFullyLoadOutput = moduleBuilder.commit((state, { documentId, cellId }) => {
        const byConvoId = utils.vueSetDefault(state.outputs, documentId, {});
        const oldState = utils.vueSetDefault(byConvoId, cellId, {
            fullyLoaded: true,
            data: [],
        });
        utils.vueSet(oldState, 'fullyLoaded', true);
    }, 'commitFullyLoadOutput');
    /** Load outputs for a cell. */
    JupyterStore.loadOutputs = moduleBuilder.dispatch(async (ctx, { documentId, cellId }) => {
        const { fullLoader } = getOutputsState(ctx.state, { documentId, cellId }) ?? {};
        if (fullLoader != null) {
            await fullLoader;
        }
        if (getOutputsState(ctx.state, { documentId, cellId })?.fullyLoaded === true) {
            return;
        }
        const loader = (async () => {
            const { paginateListEditorDocumentJupyterCellOutputs } = await import('@codegrade/apiv2-client');
            const outputWaiters = [];
            for await (const page of paginateListEditorDocumentJupyterCellOutputs({
                client: apiv2Client.getRawClient(),
            }, {
                documentId,
                cellId,
            })) {
                outputWaiters.push(Promise.all(page.outputs.map(async (output) => await processOutput(output))).then(dataToAdd => {
                    commitAddOutputData({
                        cellId,
                        documentId,
                        dataToAdd,
                    });
                }));
            }
            await Promise.all(outputWaiters);
        })();
        commitOutputLoader({ documentId, cellId, loader });
        try {
            await loader;
            JupyterStore.commitFullyLoadOutput({ documentId, cellId });
        }
        finally {
            commitOutputLoader({ documentId, cellId, loader: undefined });
        }
    }, 'loadOutputs');
    /** Add the given outputs received in websocket updates to the store. */
    JupyterStore.addWebsocketOutputs = moduleBuilder.dispatch(async (_ctx, { documentId, outputs, }) => {
        const result = await Promise.all(outputs.map(async (output) => ({
            cellId: output.cellId,
            dataToAdd: await processOutput(websocketOutputToSmithy(output)),
        })));
        const outputsByCell = new Map();
        for (const { cellId, dataToAdd } of result) {
            let outputsForCell = outputsByCell.get(cellId);
            if (outputsForCell == null) {
                outputsForCell = [];
                outputsByCell.set(cellId, outputsForCell);
            }
            outputsForCell.push(dataToAdd);
        }
        for (const [cellId, dataToAdd] of outputsByCell.entries()) {
            commitAddOutputData({
                documentId,
                cellId,
                dataToAdd,
            });
        }
    }, 'addOutputs');
    /** @returns A function that for the cell ids gives all the outputs for that cell. */
    JupyterStore.getOutputs = moduleBuilder.read(state => 
    /**
     * Get the outputs.
     *
     * @param ids The ids to get the output for.
     * @returns The found output.
     */
    (ids) => {
        const output = getOutputsState(state, ids);
        if (output == null || !output.fullyLoaded) {
            return Maybe.empty();
        }
        return Maybe.of(output.data);
    }, 'getOutputs');
    /**
     * Commit an kernel status update to the store.
     *
     * @returns Nothing.
     */
    JupyterStore.commitKernel = moduleBuilder.commit((state, { documentId, kernel, }) => {
        utils.vueSet(state.kernels, documentId, kernel);
    }, 'commitKernel');
    /**
     * Load the current status of the Jupyter kernel for the given document.
     *
     * @returns Nothing.
     */
    JupyterStore.loadKernel = moduleBuilder.dispatch(async (_ctx, { documentId, }) => {
        const { GetEditorDocumentCommand, JupyterKernelStatus } = await import('@codegrade/apiv2-client');
        let kernel;
        try {
            const { document } = await apiv2Client.send(new GetEditorDocumentCommand({ documentId }));
            kernel = document.kernel;
        }
        catch {
            kernel = {
                status: { $unknown: ['unknown', null] },
            };
        }
        JupyterStore.commitKernel({
            documentId,
            kernel: {
                status: JupyterKernelStatus.visit(kernel.status, {
                    alive: ({ kernelId }) => ({
                        tag: 'alive',
                        kernelId,
                    }),
                    dead: () => ({ tag: 'dead' }),
                    starting: () => ({ tag: 'starting' }),
                    _: () => ({ tag: 'unknown' }),
                }),
            },
        });
    }, 'loadKernel');
    /** @returns A function that gets the last known kernel status for the requested document. */
    JupyterStore.getKernel = moduleBuilder.read(state => 
    /**
     * Get the last known kernel status for the given document.
     *
     * @param documentId The id of the document to get the kernel status for.
     * @returns The current status in the store of the kernel for the given document.
     */
    (documentId) => state.kernels[documentId] ?? { status: { tag: 'unknown' } }, 'getKernel');
})(JupyterStore || (JupyterStore = {}));
